import { KeyRound, LogOut, UserRound } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { Link } from "react-router";

import { cargosDe, useSalir, useUsuario } from "./api";

// El formulario se descarga al abrirlo; la barra de cuenta sigue siendo ligera.
const ChangePasswordSheet = lazy(() => import("./ChangePasswordSheet").then(m => ({ default: m.ChangePasswordSheet })));

/**
 * Quién está dentro, y cómo salir.
 *
 * **Un ERP de planta se abre desde un celular compartido.** Sin esto no había manera de
 * cerrar la sesión ni de saber con qué cuenta se está escribiendo: quien tomara el equipo
 * seguía siendo quien lo dejó, y cada movimiento de bodega quedaba firmado con su nombre.
 *
 * El rol se enseña aunque **todavía no haga nada** (D-014: los permisos por rol llegan
 * después). Quien usa el sistema tiene que poder ver con qué cuenta entró, y el día que
 * los permisos existan la etiqueta ya estará donde la gente la busca.
 *
 * **Sin menú desplegable, a propósito.** La primera versión usaba un Popover y metía 21 KB
 * comprimidos en el paquete que se descarga siempre —un cuarto de lo que pesa toda la
 * aplicación— para esconder dos datos y un botón detrás de un toque de más. Aquí se ve la
 * cuenta y se sale de una; lo que no cabe en el celular lo tapa el CSS, no un menú.
 *
 * Salir no pregunta: no se pierde nada, porque todo lo que se escribe se guarda al
 * momento. Lo que sí hace es **vaciar la caché** (`useSalir`), donde quedaron la nómina,
 * los precios de compra y los pedidos de quien acaba de irse.
 */

const ROLES: Record<string, string> = {
  admin: "Administrador",
  planta: "Planta",
  ventas: "Ventas",
  viewer: "Solo lectura",
};

/**
 * Las iniciales del nombre: «Néstor Cantillo» → «NC». Una sola palabra da una letra.
 *
 * **Aguanta que no venga nombre, y eso no es paranoia.** Esta pieza se dibuja en TODAS las
 * pantallas, así que si revienta no se cae el menú de cuenta: se cae la aplicación entera y
 * lo único que queda es «La pantalla no cargó». Le pasó a una prueba con una respuesta de
 * `/auth/me` inesperada, y en producción bastaría un despliegue desacompasado para lo mismo.
 * Un armazón que se desmorona porque falta un nombre es una consecuencia desproporcionada.
 */
function initials(name: string | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function AccountMenu() {
  const usuario = useUsuario();
  const salir = useSalir();
  const [changingPassword, setChangingPassword] = useState(false);

  // La consulta ya la hizo el portero: aquí sale de la caché y no pide nada.
  const user = usuario.data;
  if (!user) return null;
  // Sin nombre se usa el correo: siempre hay algo con lo que identificar la sesión.
  const nombre = user.name || user.email || "Tu cuenta";

  // Una persona puede tener varios cargos —José controla bodega y formulación, son dos— así
  // que se enseñan todos. `cargosDe` da igual la respuesta nueva que la vieja.
  const cargos = cargosDe(user)
    .map((c) => ROLES[c.code] ?? c.name)
    .join(" · ");

  return (
    <div className="glass flex min-h-11 items-center gap-2 rounded-lg pl-2.5">
      {/* Un solo elemento para el lector de pantalla: la inicial es decorativa y el nombre
          se corta en pantallas angostas, así que el texto completo va en el título. */}
      <p className="flex min-w-0 items-center gap-2.5 text-light" title={`${nombre} · ${user.email ?? ""}`}>
        <span
          aria-hidden
          className="grid size-7 shrink-0 place-items-center rounded-full bg-white/10 font-mono text-xs font-semibold"
        >
          {initials(nombre)}
        </span>
        <span className="grid min-w-0 leading-tight">
          <span className="hidden max-w-40 truncate text-sm font-medium sm:block">{nombre}</span>
          <span className="hidden max-w-40 truncate text-xs text-light-3 lg:block">{cargos}</span>
          <span className="sr-only">
            Dentro como {nombre}
            {cargos ? ` · ${cargos}` : ""}
          </span>
        </span>
      </p>

      <Link to="/perfil" aria-label="Perfil" title="Perfil"
        className="grid min-h-11 min-w-11 place-items-center rounded-lg text-light-2 transition-colors hover:bg-white/10 hover:text-light">
        <UserRound aria-hidden className="size-4.5" />
      </Link>

      <button type="button" onClick={() => setChangingPassword(true)} aria-label="Mi contraseña" title="Mi contraseña"
        className="grid min-h-11 min-w-11 place-items-center rounded-lg text-light-2 transition-colors hover:bg-white/10 hover:text-light">
        <KeyRound aria-hidden className="size-4.5" />
      </button>

      <button
        type="button"
        onClick={() => salir.mutate()}
        disabled={salir.isPending}
        aria-label={salir.isPending ? "Saliendo" : "Salir"}
        title="Salir"
        className="grid min-h-11 min-w-11 place-items-center rounded-lg text-light-3 transition-colors hover:bg-white/10 hover:text-light disabled:pointer-events-none disabled:opacity-50"
      >
        <LogOut aria-hidden className="size-4.5" />
      </button>

      {salir.isError ? (
        <p role="alert" className="fixed right-4 bottom-24 z-50 max-w-[min(24rem,calc(100vw-2rem))] rounded-md border border-line bg-surface px-4 py-3 text-sm text-bad shadow-card">
          No se pudo confirmar el cierre. Pulsa «Salir» para reintentar. {(salir.error as Error).message}
        </p>
      ) : null}
      {changingPassword ? <Suspense fallback={<p role="status" className="sr-only">Abriendo contraseña…</p>}>
        <ChangePasswordSheet onClose={() => setChangingPassword(false)} />
      </Suspense> : null}
    </div>
  );
}
